import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { FaCalendarAlt, FaChevronDown, FaChevronUp, FaClipboardCheck, FaExclamationCircle, FaLayerGroup, FaPlus, FaSave, FaSortAmountDown, FaSortAmountUp, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { db as appDb } from '../../firebase/firebase';
import './PracticasRegistroPanel.scss';

const TOTAL_MODULOS = 15;
const TOTAL_EJERCICIOS = 10;
const DRAFT_PREFIX = 'draft-';

const buildBlankFicha = (alumnoId, modulo) => ({
  alumnoId,
  ejercicios: Array(TOTAL_EJERCICIOS).fill(''),
  fecha: '',
  numeroDePractica: '',
  tiempoLectura: '',
  tituloYpagsLeidas: '',
  modulo,
  fechaDeActualizacion: new Date(),
});

const normalizeFicha = (ficha) => ({
  ...ficha,
  ejercicios: Array.from({ length: TOTAL_EJERCICIOS }, (_, index) => ficha?.ejercicios?.[index] || ''),
  fecha: ficha?.fecha || '',
  numeroDePractica: ficha?.numeroDePractica || '',
  tiempoLectura: ficha?.tiempoLectura || '',
  tituloYpagsLeidas: ficha?.tituloYpagsLeidas || '',
});

const serializeFicha = (ficha) => JSON.stringify({
  fecha: ficha?.fecha || '',
  numeroDePractica: ficha?.numeroDePractica || '',
  tiempoLectura: ficha?.tiempoLectura || '',
  tituloYpagsLeidas: ficha?.tituloYpagsLeidas || '',
  modulo: ficha?.modulo || '',
  ejercicios: Array.from({ length: TOTAL_EJERCICIOS }, (_, index) => ficha?.ejercicios?.[index] || ''),
});

const isFichaBlank = (ficha) => {
  if (!ficha) return true;

  const values = [
    ficha.fecha,
    ficha.numeroDePractica,
    ficha.tiempoLectura,
    ficha.tituloYpagsLeidas,
    ...(ficha.ejercicios || []),
  ];

  return values.every((value) => String(value || '').trim() === '');
};

const isDraftFicha = (ficha) => Boolean(ficha?.isDraft || String(ficha?.id || '').startsWith(DRAFT_PREFIX));

const buildFirestorePayload = (ficha) => ({
  alumnoId: ficha.alumnoId,
  ejercicios: Array.from({ length: TOTAL_EJERCICIOS }, (_, index) => ficha?.ejercicios?.[index] || ''),
  fecha: ficha?.fecha || '',
  numeroDePractica: ficha?.numeroDePractica || '',
  tiempoLectura: ficha?.tiempoLectura || '',
  tituloYpagsLeidas: ficha?.tituloYpagsLeidas || '',
  modulo: ficha.modulo,
  fechaDeActualizacion: new Date(),
});

const buildBlankModuleSummary = (alumnoId, modulo) => ({
  alumnoId,
  modulo,
  evaluacionLectura: '',
  evaluacionTiempo: '',
  evaluacionVelocidad: '',
  evaluacionComprension: '',
  compromiso: '',
});

const normalizeModuleSummary = (summary, alumnoId, modulo) => ({
  ...buildBlankModuleSummary(alumnoId, modulo),
  ...summary,
  alumnoId,
  modulo,
  evaluacionLectura: summary?.evaluacionLectura || '',
  evaluacionTiempo: summary?.evaluacionTiempo || '',
  evaluacionVelocidad: summary?.evaluacionVelocidad || '',
  evaluacionComprension: summary?.evaluacionComprension || '',
  compromiso: summary?.compromiso || '',
});

const serializeModuleSummary = (summary) => JSON.stringify({
  evaluacionLectura: summary?.evaluacionLectura || '',
  evaluacionTiempo: summary?.evaluacionTiempo || '',
  evaluacionVelocidad: summary?.evaluacionVelocidad || '',
  evaluacionComprension: summary?.evaluacionComprension || '',
  compromiso: summary?.compromiso || '',
  modulo: summary?.modulo || '',
});

const buildModuleSummaryPayload = (summary) => ({
  alumnoId: summary.alumnoId,
  modulo: summary.modulo,
  evaluacionLectura: summary?.evaluacionLectura || '',
  evaluacionTiempo: summary?.evaluacionTiempo || '',
  evaluacionVelocidad: summary?.evaluacionVelocidad || '',
  evaluacionComprension: summary?.evaluacionComprension || '',
  compromiso: summary?.compromiso || '',
  fechaDeActualizacion: new Date(),
});

const getDateScore = (fecha) => {
  if (!fecha) return 0;
  const date = new Date(fecha);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const sortFichasByFecha = (items, direction) => [...items].sort((left, right) => {
  const delta = getDateScore(left?.fecha) - getDateScore(right?.fecha);
  if (delta === 0) {
    return (left?.numeroDePractica || '').localeCompare(right?.numeroDePractica || '');
  }

  return direction === 'asc' ? delta : -delta;
});

const PracticasRegistroPanel = ({ alumnoId, title = 'Registro de Prácticas' }) => {
  const [moduloSeleccionado, setModuloSeleccionado] = useState('1');
  const [fichas, setFichas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFichaId, setActiveFichaId] = useState(null);
  const [dirtyRows, setDirtyRows] = useState({});
  const [sortDirection, setSortDirection] = useState('desc');
  const [moduleSummary, setModuleSummary] = useState(() => buildBlankModuleSummary(alumnoId, '1'));
  const [summaryDirty, setSummaryDirty] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const rowInputRefs = useRef({});
  const savedSnapshotsRef = useRef({});
  const savedSummarySnapshotRef = useRef(serializeModuleSummary(buildBlankModuleSummary(alumnoId, '1')));
  const switchingRowRef = useRef(false);

  const modulos = useMemo(
    () => Array.from({ length: TOTAL_MODULOS }, (_, index) => `${index + 1}`),
    [],
  );

  const focusRow = (fichaId) => {
    if (!fichaId) return;
    const target = rowInputRefs.current[fichaId];
    if (target && typeof target.focus === 'function') {
      window.setTimeout(() => target.focus(), 0);
    }
  };

  const refreshSnapshots = (items) => {
    savedSnapshotsRef.current = items.reduce((accumulator, ficha) => {
      accumulator[ficha.id] = serializeFicha(ficha);
      return accumulator;
    }, {});
    setDirtyRows({});
  };

  const refreshSummarySnapshot = (summary) => {
    savedSummarySnapshotRef.current = serializeModuleSummary(summary);
    setSummaryDirty(false);
  };

  const removeFichaLocally = (fichaId) => {
    setFichas((current) => current.filter((item) => item.id !== fichaId));
    setDirtyRows((previous) => {
      const next = { ...previous };
      delete next[fichaId];
      return next;
    });
    delete savedSnapshotsRef.current[fichaId];
    if (activeFichaId === fichaId) {
      setActiveFichaId(null);
    }
  };

  const fetchFichas = useCallback(async () => {
    if (!alumnoId) {
      setFichas([]);
      setLoading(false);
      setSummaryLoading(false);
      return;
    }

    setLoading(true);
    setSummaryLoading(true);
    try {
      const fichasRef = collection(appDb, 'fichas');
      const moduleSummaryRef = collection(appDb, 'practicasResumenModulo');
      const currentQuery = query(
        fichasRef,
        where('alumnoId', '==', alumnoId),
        where('modulo', '==', moduloSeleccionado),
      );
      const summaryQuery = query(
        moduleSummaryRef,
        where('alumnoId', '==', alumnoId),
        where('modulo', '==', moduloSeleccionado),
      );
      const snapshot = await getDocs(currentQuery);
      const summarySnapshot = await getDocs(summaryQuery);
      const items = snapshot.docs
        .map((item) => normalizeFicha({ id: item.id, ...item.data() }))
        .filter((item) => !isFichaBlank(item));
      const summaryDoc = summarySnapshot.docs[0];
      const currentSummary = normalizeModuleSummary(
        summaryDoc ? { id: summaryDoc.id, ...summaryDoc.data() } : null,
        alumnoId,
        moduloSeleccionado,
      );
      const orderedItems = sortFichasByFecha(items, sortDirection);
      setFichas(orderedItems);
      setModuleSummary(currentSummary);
      setActiveFichaId(null);
      setSummaryLoading(false);
      refreshSnapshots(orderedItems);
      refreshSummarySnapshot(currentSummary);
    } catch (error) {
      console.error('Error fetching fichas:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al cargar',
        text: 'No fue posible cargar las prácticas del módulo seleccionado.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  }, [alumnoId, moduloSeleccionado, sortDirection]);

  useEffect(() => {
    void fetchFichas();
  }, [fetchFichas]);

  useEffect(() => {
    setFichas((current) => sortFichasByFecha(current, sortDirection));
  }, [sortDirection]);

  const handleFichaChange = (fichaId, field, value) => {
    setFichas((current) => current.map((ficha) => {
      if (ficha.id !== fichaId) return ficha;

      const updatedFicha = { ...ficha };
      const fieldParts = field.split('[');

      if (fieldParts.length > 1) {
        const fieldName = fieldParts[0];
        const fieldIndex = parseInt(fieldParts[1].replace(']', ''), 10);
        updatedFicha[fieldName] = [...(updatedFicha[fieldName] || Array(TOTAL_EJERCICIOS).fill(''))];
        updatedFicha[fieldName][fieldIndex] = value;
      } else {
        updatedFicha[field] = value;
      }

      const savedSnapshot = savedSnapshotsRef.current[fichaId];
      const isDirty = serializeFicha(updatedFicha) !== savedSnapshot;
      setDirtyRows((previous) => ({ ...previous, [fichaId]: isDirty }));

      return updatedFicha;
    }));
  };

  const handleModuleSummaryChange = (field, value) => {
    setModuleSummary((current) => {
      const updatedSummary = { ...current, [field]: value };
      setSummaryDirty(serializeModuleSummary(updatedSummary) !== savedSummarySnapshotRef.current);
      return updatedSummary;
    });
  };

  const saveModuleSummary = async (options = {}) => {
    const { showSuccess = true } = options;

    try {
      const payload = buildModuleSummaryPayload(moduleSummary);

      if (moduleSummary?.id) {
        await updateDoc(doc(appDb, 'practicasResumenModulo', moduleSummary.id), payload);
        const persistedSummary = normalizeModuleSummary({ ...payload, id: moduleSummary.id }, alumnoId, moduloSeleccionado);
        setModuleSummary(persistedSummary);
        refreshSummarySnapshot(persistedSummary);
      } else {
        const docRef = await addDoc(collection(appDb, 'practicasResumenModulo'), payload);
        const persistedSummary = normalizeModuleSummary({ ...payload, id: docRef.id }, alumnoId, moduloSeleccionado);
        setModuleSummary(persistedSummary);
        refreshSummarySnapshot(persistedSummary);
      }

      if (showSuccess) {
        Swal.fire({
          title: 'Guardado',
          text: 'El resumen del módulo se actualizó correctamente.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar',
        });
      }

      return true;
    } catch (error) {
      console.error('Error al guardar el resumen del módulo:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: 'No fue posible guardar la información adicional del módulo.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
  };

  const restoreModuleSummary = () => {
    const snapshot = savedSummarySnapshotRef.current;
    const restoredSummary = normalizeModuleSummary(JSON.parse(snapshot), alumnoId, moduloSeleccionado);
    setModuleSummary((current) => ({ ...current, ...restoredSummary, id: current?.id }));
    setSummaryDirty(false);
  };

  const saveFichaById = async (fichaId, options = {}) => {
    const { showSuccess = true } = options;
    const ficha = fichas.find((item) => item.id === fichaId);

    if (!ficha?.id) return false;
    if (isFichaBlank(ficha)) {
      Swal.fire({
        icon: 'warning',
        title: 'Fila vacía',
        text: 'No se puede guardar una fila en blanco. Agrega al menos un dato o elimínala.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
      focusRow(fichaId);
      return false;
    }

    try {
      if (isDraftFicha(ficha)) {
        const payload = buildFirestorePayload(ficha);
        const docRef = await addDoc(collection(appDb, 'fichas'), payload);
        const persistedFicha = normalizeFicha({ ...payload, id: docRef.id, isDraft: false });

        setFichas((current) => sortFichasByFecha(
          current.map((item) => (item.id === fichaId ? persistedFicha : item)),
          sortDirection,
        ));

        delete savedSnapshotsRef.current[fichaId];
        savedSnapshotsRef.current[docRef.id] = serializeFicha(persistedFicha);
        setDirtyRows((previous) => {
          const next = { ...previous };
          delete next[fichaId];
          next[docRef.id] = false;
          return next;
        });

        if (activeFichaId === fichaId) {
          setActiveFichaId(docRef.id);
        }
      } else {
        const payload = buildFirestorePayload(ficha);
        await updateDoc(doc(appDb, 'fichas', ficha.id), payload);
        savedSnapshotsRef.current[ficha.id] = serializeFicha(ficha);
        setDirtyRows((previous) => ({ ...previous, [ficha.id]: false }));
      }

      if (showSuccess) {
        Swal.fire({
          title: 'Guardado',
          text: 'La práctica se actualizó correctamente.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar',
        });
      }

      return true;
    } catch (error) {
      console.error('Error al guardar ficha:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: 'Ocurrió un problema al guardar los cambios.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
  };

  const restoreFicha = (fichaId) => {
    const snapshot = savedSnapshotsRef.current[fichaId];
    if (!snapshot) return;

    const restoredData = JSON.parse(snapshot);
    setFichas((current) => current.map((ficha) => (
      ficha.id === fichaId ? normalizeFicha({ ...ficha, ...restoredData }) : ficha
    )));
    setDirtyRows((previous) => ({ ...previous, [fichaId]: false }));
  };

  const confirmLeaveCurrentRow = async () => {
    const currentFicha = fichas.find((item) => item.id === activeFichaId);
    if (!currentFicha) {
      return true;
    }

    const isDraft = isDraftFicha(currentFicha);
    const isBlank = isFichaBlank(currentFicha);
    const isDirty = Boolean(dirtyRows[activeFichaId]);

    if (!isDraft && !isDirty) {
      return true;
    }

    if (isDraft && isBlank) {
      const result = await Swal.fire({
        title: 'La fila nueva está vacía',
        text: 'Para continuar, esta fila se debe descartar.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Descartar y continuar',
        cancelButtonText: 'Seguir editando',
        confirmButtonColor: '#0d6efd',
        cancelButtonColor: '#198754',
      });

      if (result.isConfirmed) {
        removeFichaLocally(activeFichaId);
        return true;
      }

      focusRow(activeFichaId);
      return false;
    }

    const result = await Swal.fire({
      title: 'Tienes cambios sin guardar',
      text: isDraft
        ? 'Quieres guardar esta nueva fila antes de continuar?'
        : 'Quieres guardar esta fila antes de continuar?',
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Guardar y continuar',
      denyButtonText: 'Continuar sin guardar',
      cancelButtonText: 'Seguir editando',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#6c757d',
      cancelButtonColor: '#198754',
    });

    if (result.isConfirmed) {
      return saveFichaById(activeFichaId, { showSuccess: false });
    }

    if (result.isDenied) {
      if (isDraft) {
        removeFichaLocally(activeFichaId);
      } else {
        restoreFicha(activeFichaId);
      }
      return true;
    }

    focusRow(activeFichaId);
    return false;
  };

  const confirmLeaveModuleSummary = async () => {
    if (!summaryDirty) {
      return true;
    }

    const result = await Swal.fire({
      title: 'Tienes cambios sin guardar',
      text: 'Quieres guardar la información adicional del módulo antes de continuar?',
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Guardar y continuar',
      denyButtonText: 'Continuar sin guardar',
      cancelButtonText: 'Seguir editando',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#6c757d',
      cancelButtonColor: '#198754',
    });

    if (result.isConfirmed) {
      return saveModuleSummary({ showSuccess: false });
    }

    if (result.isDenied) {
      restoreModuleSummary();
      return true;
    }

    return false;
  };

  const activateRow = async (fichaId) => {
    if (!fichaId || fichaId === activeFichaId || switchingRowRef.current) {
      return;
    }

    switchingRowRef.current = true;
    const canContinue = await confirmLeaveCurrentRow();
    switchingRowRef.current = false;

    if (canContinue) {
      setActiveFichaId(fichaId);
      return;
    }

    focusRow(activeFichaId);
  };

  const handleModuleChange = async (nextModulo) => {
    if (nextModulo === moduloSeleccionado) return;

    const canLeaveRow = await confirmLeaveCurrentRow();
    if (!canLeaveRow) return;
    const canLeaveSummary = await confirmLeaveModuleSummary();
    if (!canLeaveSummary) return;

    setActiveFichaId(null);
    setModuloSeleccionado(nextModulo);
  };

  const handleAddFicha = async () => {
    if (!alumnoId) {
      Swal.fire({
        icon: 'error',
        title: 'Alumno no disponible',
        text: 'No fue posible identificar al alumno para crear la práctica.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    const canContinue = await confirmLeaveCurrentRow();
    if (!canContinue) return;

    try {
      const existingDraft = fichas.find((item) => isDraftFicha(item));
      if (existingDraft) {
        focusRow(existingDraft.id);
        Swal.fire({
          icon: 'info',
          title: 'Fila pendiente',
          text: 'Primero guarda o descarta la fila nueva antes de crear otra.',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar',
        });
        return;
      }

      const draftId = `${DRAFT_PREFIX}${Date.now()}`;
      const draftFicha = normalizeFicha({
        ...buildBlankFicha(alumnoId, moduloSeleccionado),
        id: draftId,
        isDraft: true,
      });

      setFichas((current) => sortFichasByFecha([...current, draftFicha], sortDirection));
      savedSnapshotsRef.current[draftFicha.id] = serializeFicha(draftFicha);
      setDirtyRows((previous) => ({ ...previous, [draftFicha.id]: false }));
      setActiveFichaId(draftFicha.id);
      focusRow(draftFicha.id);
    } catch (error) {
      console.error('Error al añadir ficha:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al añadir',
        text: 'Ocurrió un error al intentar añadir la ficha.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const handleDeleteFicha = async (fichaId) => {
    const ficha = fichas.find((item) => item.id === fichaId);
    if (!ficha?.id) return;

    if (isDraftFicha(ficha)) {
      removeFichaLocally(ficha.id);
      return;
    }

    try {
      await deleteDoc(doc(appDb, 'fichas', ficha.id));
      removeFichaLocally(ficha.id);

      Swal.fire({
        title: 'Eliminado',
        text: 'Ficha eliminada con éxito.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
    } catch (error) {
      console.error('Error al eliminar ficha:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar',
        text: 'Ocurrió un error al intentar eliminar la ficha.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const sortButtonLabel = sortDirection === 'desc' ? 'Mas recientes primero' : 'Mas antiguas primero';

  return (
    <div className="practicas-panel">
      <Card className="practicas-panel__hero border-0 shadow-sm mb-4">
        <Card.Body className="p-4">
          <div className="practicas-panel__hero-top">
            <div>
              <span className="practicas-panel__eyebrow">Seguimiento academico</span>
              <h2 className="practicas-panel__title mb-2">{title}</h2>
              <p className="practicas-panel__subtitle mb-0">
                Selecciona el modulo activo, ordena por fecha y gestiona cada fila con guardado controlado.
              </p>
            </div>
            <div className="practicas-panel__summary">
              <span>Modulo activo</span>
              <strong>{`M${moduloSeleccionado.padStart(2, '0')}`}</strong>
              <small>{`${fichas.length} practica${fichas.length === 1 ? '' : 's'} registradas`}</small>
            </div>
          </div>

          <div className="practicas-panel__modules mt-4">
            <div className="practicas-panel__modules-label">
              <FaLayerGroup />
              <span>Modulos</span>
            </div>
            <div className="practicas-panel__modules-grid">
              {modulos.map((modulo) => (
                <button
                  key={modulo}
                  type="button"
                  className={`practicas-panel__module-chip ${modulo === moduloSeleccionado ? 'is-active' : ''}`}
                  onClick={() => {
                    void handleModuleChange(modulo);
                  }}
                >
                  <span className="practicas-panel__module-chip-label">Modulo</span>
                  <strong>{modulo}</strong>
                </button>
              ))}
            </div>
          </div>

          <div className="practicas-panel__toolbar mt-4">
            <Button
              variant="light"
              className="practicas-panel__toolbar-button"
              onClick={() => setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'))}
            >
              {sortDirection === 'desc' ? <FaSortAmountDown className="me-2" /> : <FaSortAmountUp className="me-2" />}
              Ordenar fecha: {sortButtonLabel}
            </Button>
            <Button variant="success" className="practicas-panel__toolbar-button" onClick={() => { void handleAddFicha(); }}>
              <FaPlus className="me-2" />
              Anadir ficha
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 mb-0">Cargando practicas...</p>
            </div>
          ) : fichas.length === 0 ? (
            <div className="practicas-panel__empty-state">
              <FaExclamationCircle className="mb-3" />
              <h5>No hay practicas en este modulo</h5>
              <p className="mb-0">Agrega una ficha nueva para comenzar a registrar el avance del alumno.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="practicas-panel__table align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: '150px' }}>
                      <div className="practicas-panel__header-cell">
                        <FaCalendarAlt />
                        <span>Fecha</span>
                      </div>
                    </th>
                    <th style={{ minWidth: '220px' }}>Titulo y paginas leidas</th>
                    <th style={{ minWidth: '140px' }}>No. de practica</th>
                    <th style={{ minWidth: '140px' }}>Tiempo de lectura</th>
                    {Array.from({ length: TOTAL_EJERCICIOS }, (_, index) => (
                      <th key={index} style={{ minWidth: '100px' }}>{`Ej. ${index + 1}`}</th>
                    ))}
                    <th className="practicas-panel__actions-column" style={{ minWidth: '220px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {fichas.map((ficha) => (
                    <tr
                      key={ficha.id}
                      className={ficha.id === activeFichaId ? 'is-active' : ''}
                      onFocusCapture={() => {
                        void activateRow(ficha.id);
                      }}
                    >
                      <td>
                        <Form.Control
                          ref={(element) => {
                            if (element) {
                              rowInputRefs.current[ficha.id] = element;
                            }
                          }}
                          type="date"
                          value={ficha.fecha}
                          onChange={(event) => handleFichaChange(ficha.id, 'fecha', event.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="Titulo..."
                          value={ficha.tituloYpagsLeidas}
                          onChange={(event) => handleFichaChange(ficha.id, 'tituloYpagsLeidas', event.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="No."
                          value={ficha.numeroDePractica}
                          onChange={(event) => handleFichaChange(ficha.id, 'numeroDePractica', event.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="Ej: 5 min"
                          value={ficha.tiempoLectura}
                          onChange={(event) => handleFichaChange(ficha.id, 'tiempoLectura', event.target.value)}
                        />
                      </td>
                      {ficha.ejercicios.map((ejercicio, exerciseIndex) => (
                        <td key={`${ficha.id}-${exerciseIndex}`}>
                          <Form.Control
                            type="text"
                            value={ejercicio}
                            onChange={(event) => handleFichaChange(ficha.id, `ejercicios[${exerciseIndex}]`, event.target.value)}
                          />
                        </td>
                      ))}
                      <td className="practicas-panel__actions-column">
                        <div className="practicas-panel__actions">
                          <Button
                            variant="warning"
                            onClick={() => {
                              void saveFichaById(ficha.id);
                            }}
                          >
                            <FaSave className="me-2" />
                            Guardar
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => {
                              void handleDeleteFicha(ficha.id);
                            }}
                          >
                            <FaTrash className="me-2" />
                            Eliminar
                          </Button>
                          {dirtyRows[ficha.id] && <span className="practicas-panel__dirty-badge">Cambios sin guardar</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="practicas-panel__module-summary border-0 shadow-sm mt-4">
        <Card.Body className="p-0">
          <button
            type="button"
            className="practicas-panel__module-summary-toggle"
            onClick={() => setSummaryOpen((current) => !current)}
          >
            <div className="practicas-panel__module-summary-toggle-content">
              <span className="practicas-panel__module-summary-eyebrow">Complemento del módulo</span>
              <h3 className="mb-1">Evaluación del capítulo y compromiso</h3>
              <p className="mb-0">
                Registra la lectura, tiempo, velocidad, comprensión y el compromiso asociado al módulo actual.
              </p>
            </div>
            <div className="practicas-panel__module-summary-toggle-action">
              <span className="practicas-panel__module-summary-chip">
                <FaClipboardCheck className="me-2" />
                {summaryDirty ? 'Cambios pendientes' : 'Resumen del módulo'}
              </span>
              {summaryOpen ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </button>

          {summaryOpen && (
            <div className="practicas-panel__module-summary-body">
              {summaryLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" size="sm" />
                  <p className="mt-3 mb-0">Cargando información del módulo...</p>
                </div>
              ) : (
                <>
                  <div className="practicas-panel__module-summary-grid">
                    <div className="practicas-panel__module-summary-panel">
                      <div className="practicas-panel__module-summary-heading">Evaluación Capítulo</div>
                      <div className="practicas-panel__module-summary-fields">
                        <Form.Group>
                          <Form.Label>Lectura</Form.Label>
                          <Form.Control
                            type="text"
                            value={moduleSummary.evaluacionLectura}
                            onChange={(event) => handleModuleSummaryChange('evaluacionLectura', event.target.value)}
                            placeholder="Observación de lectura"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Tiempo</Form.Label>
                          <Form.Control
                            type="text"
                            value={moduleSummary.evaluacionTiempo}
                            onChange={(event) => handleModuleSummaryChange('evaluacionTiempo', event.target.value)}
                            placeholder="Ej. 12 min o STD"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Velocidad</Form.Label>
                          <Form.Control
                            type="text"
                            value={moduleSummary.evaluacionVelocidad}
                            onChange={(event) => handleModuleSummaryChange('evaluacionVelocidad', event.target.value)}
                            placeholder="Resultado o comentario"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Comprensión</Form.Label>
                          <Form.Control
                            type="text"
                            value={moduleSummary.evaluacionComprension}
                            onChange={(event) => handleModuleSummaryChange('evaluacionComprension', event.target.value)}
                            placeholder="Resultado o comentario"
                          />
                        </Form.Group>
                      </div>
                    </div>

                    <div className="practicas-panel__module-summary-panel">
                      <div className="practicas-panel__module-summary-heading">Compromiso</div>
                      <Form.Control
                        as="textarea"
                        rows={7}
                        value={moduleSummary.compromiso}
                        onChange={(event) => handleModuleSummaryChange('compromiso', event.target.value)}
                        placeholder="Acuerdos, observaciones y compromisos para el alumno o la familia"
                      />
                    </div>
                  </div>

                  <div className="practicas-panel__module-summary-footer">
                    <span className="practicas-panel__std-note">* STD: Sin tiempo determinado.</span>
                    <div className="practicas-panel__module-summary-footer-actions">
                      {summaryDirty && <span className="practicas-panel__dirty-badge">Cambios sin guardar</span>}
                      <Button variant="primary" onClick={() => { void saveModuleSummary(); }}>
                        <FaSave className="me-2" />
                        Guardar resumen
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PracticasRegistroPanel;
